import { useCallback, useEffect, useState } from "react";
import { Boxes, ChevronRight, Loader2 } from "lucide-react";
import { api, apiError, inr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { StatusLegend, StatusPill } from "@/components/StockStatus";
import { toast } from "sonner";

function qtyLine(node) {
  const parts = Object.entries(node.quantity_by_unit || {}).map(([u, q]) => `${q} ${u}`);
  const content = Object.entries(node.content_by_unit || {}).map(([u, q]) => `${q} ${u}`);
  return [parts.join(" • "), content.length ? `(${content.join(" • ")})` : ""]
    .filter(Boolean).join(" ");
}

export default function InventoryCatalog({ onOpenItem }) {
  const [data, setData] = useState(null);
  const [openCat, setOpenCat] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/inventory/catalog-tree");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const active = data.tree.filter((c) => c.status === "active" || c.item_count > 0);
  const current = active.find((c) => c.name === openCat);

  return (
    <div className="space-y-4" data-testid="inventory-catalog">
      <StatusLegend />

      {!current ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-cards">
          {active.map((c) => (
            <button key={c.name} type="button" data-testid={`catalog-card-${c.name}`}
              onClick={() => setOpenCat(c.name)}
              className="surface flex items-start justify-between gap-2 p-4 text-left transition-colors hover:border-primary/40 hover:bg-secondary/40">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-primary" />
                  <span className="truncate font-medium">{c.name}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {c.item_count} item(s){c.subs.length ? ` • ${c.subs.length} group(s)` : ""}
                </div>
                <div className="mt-1 font-mono-num text-xs">{qtyLine(c) || "nothing yet"}</div>
                {data.can_see_cost && c.value ? (
                  <div className="mt-0.5 text-xs text-muted-foreground">{inr(c.value)}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {c.alerts > 0 && (
                  <Badge variant="destructive" className="text-[9px]">{c.alerts} to check</Badge>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <button type="button" data-testid="catalog-back"
            className="text-xs text-muted-foreground hover:underline"
            onClick={() => setOpenCat(null)}>
            ← All categories
          </button>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-display text-base font-semibold md:text-lg">{current.name}</h2>
            <span className="font-mono-num text-xs text-muted-foreground">{qtyLine(current)}</span>
          </div>

          {[...current.subs.filter((s) => s.item_count > 0),
            ...(current.items.length ? [{ name: "Others", items: current.items, item_count: current.items.length, quantity_by_unit: current.quantity_by_unit, content_by_unit: {}, value: 0 }] : []),
          ].map((s) => (
            <div key={s.name} className="surface p-4" data-testid={`catalog-group-${s.name}`}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="overline">{s.name}</span>
                <span className="font-mono-num text-[11px] text-muted-foreground">{qtyLine(s)}</span>
              </div>
              {s.items.map((i) => (
                <button key={i.id} type="button" data-testid={`catalog-item-${i.id}`}
                  onClick={() => onOpenItem?.(i.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 border-b border-border py-2 text-left text-xs last:border-0 hover:bg-secondary/40">
                  <span className="min-w-0">
                    <span className="font-medium">{i.name}</span>
                    {i.variant && <span className="ml-1 text-muted-foreground">{i.variant}</span>}
                    <div className="text-[10px] text-muted-foreground">{i.warehouse_name}</div>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-mono-num">
                      {i.primary}
                      {i.secondary ? <span className="ml-1 text-muted-foreground">({i.secondary})</span> : null}
                    </span>
                    {data.can_see_cost && i.value ? (
                      <span className="font-mono-num text-muted-foreground">{inr(i.value)}</span>
                    ) : null}
                    <StatusPill status={i.status} testId={`catalog-status-${i.id}`} />
                  </span>
                </button>
              ))}
              {s.items.length === 0 && (
                <p className="text-xs text-muted-foreground">Nothing in this group yet.</p>
              )}
            </div>
          ))}

          {current.item_count === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="catalog-empty">
              No items in {current.name} yet. Use Add Item to put one in.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
